import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ProductService from "../../../api/productService";
import axios from "axios";
import { CATEGORIES, OTHER, WS_CONFIG } from "../../../config/api.config";
import { getApiUrl } from "../../../config/api";
import { ChevronLeft, SlidersHorizontal, Heart } from 'lucide-react';

// Image Gallery Modal Component
const ImageGalleryModal = ({ isOpen, onClose, images, productName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!isOpen) return null;

  const prevImage = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="relative max-w-4xl w-full bg-white rounded-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        <button 
          onClick={onClose}
          className="absolute top-2 right-2 text-white bg-black bg-opacity-50 rounded-full p-2 z-10"
        >
          ✕
        </button>
        
        <div className="relative h-[70vh]">
          <img
            src={images[currentIndex]}
            alt={`${productName} - Image ${currentIndex + 1}`}
            className="w-full h-full object-contain"
          />
          
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
              >
                ❮
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
              >
                ❯
              </button>
              
              <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex(index);
                    }}
                    className={`w-2 h-2 rounded-full ${
                      index === currentIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
              
              <div className="absolute bottom-4 right-4 text-white text-sm bg-black bg-opacity-50 px-2 py-1 rounded">
                {currentIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ViewProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [caratPrices, setCaratPrices] = useState({});
  const [calculatedPrices, setCalculatedPrices] = useState({});
  const [wsStatus, setWsStatus] = useState("Initializing...");
  const [usePolling, setUsePolling] = useState(false);
  const socketRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const navigate = useNavigate();

  // Favorites state
  const [favorites, setFavorites] = useState(new Set());
  
  // State for image gallery
  const [galleryState, setGalleryState] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0,
    productName: ''
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(12);

  // Filter states
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedCarat, setSelectedCarat] = useState("all");
  
  // Open gallery with product images
  const openGallery = useCallback((product) => {
    const images = [
      ...(product.coverImage ? [product.coverImage] : []),
      ...(product.images || [])
    ].filter(Boolean);
    
    if (images.length > 0) {
      setGalleryState({
        isOpen: true,
        images,
        currentIndex: 0,
        productName: product.name
      });
    }
  }, []);
  
  // Close gallery
  const closeGallery = useCallback(() => {
    setGalleryState(prev => ({ ...prev, isOpen: false }));
  }, []);
  
  // Navigate gallery images
  const navigateGallery = useCallback((direction) => {
    setGalleryState(prev => {
      if (direction === 'next') {
        const newIndex = (prev.currentIndex + 1) % prev.images.length;
        return { ...prev, currentIndex: newIndex };
      } else {
        const newIndex = (prev.currentIndex - 1 + prev.images.length) % prev.images.length;
        return { ...prev, currentIndex: newIndex };
      }
    });
  }, []);

  const location = useLocation();

  // Function to update URL parameters
  const updateURL = useCallback((category, carat) => {
    const params = new URLSearchParams();
    
    if (category && category !== "all") {
      params.set("category", category);
    }
    
    if (carat && carat !== "all") {
      params.set("carat", carat);
    }
    
    const newURL = params.toString() ? `/shop/?${params.toString()}` : "/shop";
    navigate(newURL, { replace: true });
  }, [navigate]);

  // Handle URL parameters on component mount and URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get("category");
    const caratParam = params.get("carat");

    console.log('🔗 URL PARAMETERS:', { categoryParam, caratParam, search: location.search });

    if (categoryParam) {
      console.log('🎯 Setting category from URL:', categoryParam);
      setSelectedCategory(categoryParam);
    } else {
      setSelectedCategory("all");
    }

    if (caratParam) {
      setSelectedCarat(caratParam);
    } else {
      setSelectedCarat("all");
    }
  }, [location.search]);

  // Replace the existing favorites loading useEffect with this:
  useEffect(() => {
    // First try to load from API, fallback to localStorage
    const loadFavorites = async () => {
      try {
        await fetchUserWishlist();
      } catch (error) {
        // Fallback to localStorage if API fails
        const savedFavorites = localStorage.getItem("jewelryFavorites");
        if (savedFavorites) {
          try {
            const favoritesArray = JSON.parse(savedFavorites);
            setFavorites(new Set(favoritesArray));
          } catch (error) {
            console.error("Error loading favorites from localStorage:", error);
          }
        }
      }
    };

    loadFavorites();
  }, []);

  // Save favorites to localStorage whenever favorites change
  useEffect(() => {
    try {
      localStorage.setItem("jewelryFavorites", JSON.stringify([...favorites]));
    } catch (error) {
      console.error("Error saving favorites to localStorage:", error);
    }
  }, [favorites]);

  // Toggle favorite status
  // Replace your existing toggleFavorite function with this:
  const toggleFavorite = async (productId, event) => {
    // Prevent card click event from firing
    event.stopPropagation();

    try {
      const isFavorited = favorites.has(productId);

      if (isFavorited) {
        // Remove from wishlist
        await removeFromWishlist(productId);
        setFavorites((prev) => {
          const newFavorites = new Set(prev);
          newFavorites.delete(productId);
          return newFavorites;
        });
      } else {
        // Add to wishlist
        await addToWishlist(productId);
        setFavorites((prev) => {
          const newFavorites = new Set(prev);
          newFavorites.add(productId);
          return newFavorites;
        });
      }
    } catch (error) {
      console.error("Error updating wishlist:", error);
      // Show error message to user (you can add a toast notification here)
      alert("Failed to update wishlist. Please try again.");
    }
  };
  // Memoize the price calculation function
  const calculateProductPrices = useCallback((productsList, prices) => {
    console.log("Calculating prices with gold prices:", prices);
    if (!Object.keys(prices).length || !productsList.length) return;

    const newPrices = {};
    productsList.forEach((product) => {
      try {
        // Use consistent property names
        const netWeight = product.netWeight || 0;
        const carat = product.carat || "";
        const makingCharge = parseFloat(product.makingcharge) || 0;
        const makingChargesPerGram = parseFloat(product.makingChargesPerGram) || 0;

        if (netWeight && carat && !isNaN(netWeight)) {
          const caratKey = carat.toUpperCase();
          const pricePerGram = parseFloat(prices[caratKey]);

          // Make sure we have a valid price
          if (!pricePerGram || isNaN(pricePerGram)) {
            console.warn(
              `Invalid price for ${caratKey}:`,
              prices[caratKey]
            );
            return;
          }

          // Different pricing calculation for silver products
          if (carat === "Silver") {
            // Silver pricing: (silverPricePerGram * silverWeight) + (makingChargesPerGram * grams)
            const silverPrice = netWeight * pricePerGram;
            const makingChargeAmount = makingChargesPerGram * netWeight;
            const totalPrice = silverPrice + makingChargeAmount;

            // Store the calculated price with detailed breakdown for debugging
            newPrices[product._id] = {
              total: totalPrice.toFixed(2),
              breakdown: {
                silverPrice: silverPrice.toFixed(2),
                makingChargeAmount: makingChargeAmount.toFixed(2),
                netWeight,
                pricePerGram: pricePerGram,
                makingChargesPerGram: makingChargesPerGram,
                pricingType: "silver",
              },
            };

            console.log(
              `Silver price calculated for ${product.name}: ₹${totalPrice.toFixed(2)} (Silver: ₹${silverPrice.toFixed(2)} + Making: ₹${makingChargeAmount.toFixed(2)})`
            );
          } else {
            // Gold pricing: (weight * pricePerGram) + (goldPrice * makingCharge%)
            const goldPrice = netWeight * pricePerGram;
            const makingChargeAmount = (goldPrice * makingCharge) / 100;
            const totalPrice = goldPrice + makingChargeAmount;

            // Store the calculated price with detailed breakdown for debugging
            newPrices[product._id] = {
              total: totalPrice.toFixed(2),
              breakdown: {
                goldPrice: goldPrice.toFixed(2),
                makingChargeAmount: makingChargeAmount.toFixed(2),
                netWeight,
                pricePerGram: pricePerGram,
                makingChargePercentage: makingCharge,
                pricingType: "gold",
              },
            };

            console.log(
              `Gold price calculated for ${product.name}: ₹${totalPrice.toFixed(2)}`
            );
          }
        } else {
          console.warn("Missing required product data for price calculation:", {
            netWeight,
            carat,
            makingCharge,
            productId: product._id,
          });
        }
      } catch (err) {
        console.error("Error calculating price for product:", product._id, err);
      }
    });

    console.log("New calculated prices:", newPrices);
    setCalculatedPrices(newPrices);
  }, []);

  const fetchProducts = useCallback(async () => {
    // Dummy Data Fallback for Products
    const dummyProductsList = [
      {
        _id: "dummy1",
        name: "Classic Floral Gold Necklace",
        category: "Necklace",
        description: "A beautiful floral design 22K gold necklace.",
        carat: "22K",
        netWeight: 35.5,
        grossWeight: 36.0,
        price: 245000,
        coverImage: "https://images.unsplash.com/photo-1599643478524-fb66f70d00f8?auto=format&fit=crop&q=80&w=600"
      },
      {
        _id: "dummy2",
        name: "Traditional Temple Haar",
        category: "Necklace",
        description: "Handcrafted temple jewelry for weddings.",
        carat: "22K",
        netWeight: 45.0,
        grossWeight: 46.5,
        price: 325000,
        coverImage: "https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?auto=format&fit=crop&q=80&w=600"
      },
      {
        _id: "dummy3",
        name: "Elegant Wedding Rings",
        category: "Ring",
        description: "18K Gold diamond studded couple rings.",
        carat: "18K",
        netWeight: 8.5,
        grossWeight: 8.5,
        price: 85000,
        coverImage: "https://images.unsplash.com/photo-1605100804763-247f67b2548e?auto=format&fit=crop&q=80&w=600"
      },
      {
        _id: "dummy4",
        name: "Gold Jhumka Earrings",
        category: "Earrings",
        description: "Classic 22K gold jhumki earrings.",
        carat: "22K",
        netWeight: 12.0,
        grossWeight: 12.5,
        price: 62000,
        coverImage: "https://images.unsplash.com/photo-1535632787350-4e68ef0ac584?auto=format&fit=crop&q=80&w=600"
      },
      {
        _id: "dummy5",
        name: "Bridal Gold Set",
        category: "Bridal Set",
        description: "Complete 24K gold bridal set.",
        carat: "24K",
        netWeight: 120.0,
        grossWeight: 125.0,
        price: 985000,
        coverImage: "https://images.unsplash.com/photo-1543294001-f7cd5d7fb516?auto=format&fit=crop&q=80&w=600"
      },
      {
        _id: "dummy6",
        name: "Gold Bangles Set",
        category: "Bangles",
        description: "Set of 4 elegant 22K gold bangles.",
        carat: "22K",
        netWeight: 65.0,
        grossWeight: 66.0,
        price: 512000,
        coverImage: "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?auto=format&fit=crop&q=80&w=600"
      }
    ];

    try {
      console.log('Starting to fetch products...');
      setIsLoading(true);
      setError(null);
      
      const productsData = await ProductService.getProducts();
      
      let validProducts = Array.isArray(productsData) ? productsData : [];
      
      if (validProducts.length === 0) {
        console.log('No products found from API, using dummy data.');
        validProducts = dummyProductsList;
      }
      
      setProducts(validProducts);
      return validProducts;
    } catch (error) {
      console.error('Error fetching products, using dummy data fallback:', error);
      setProducts(dummyProductsList);
      setIsLoading(false);
      return dummyProductsList;
    } finally {
      setIsLoading(false);
    }
  }, [products, navigate]);

  // Function to validate gold price
  const validateGoldPrice = useCallback((prices, caratKey) => {
    if (!prices || !caratKey) return false;
    
    const goldPricePerGram = prices[caratKey];
    if (!goldPricePerGram || isNaN(goldPricePerGram)) {
      console.warn(
        `Invalid gold price for ${caratKey}:`,
        prices[caratKey]
      );
      return false;
    }
    return true;
  }, []);

  // Fetch categories from API
  const fetchCategories = useCallback(async () => {
    const dummyCategories = [
      { _id: 'all', name: 'All Collections', title: 'All Collections' },
      { _id: 'c1', name: 'Necklace', title: 'Necklace' },
      { _id: 'c2', name: 'Ring', title: 'Ring' },
      { _id: 'c3', name: 'Earrings', title: 'Earrings' },
      { _id: 'c4', name: 'Bridal Set', title: 'Bridal Set' },
      { _id: 'c5', name: 'Bangles', title: 'Bangles' }
    ];

    try {
      setCategoriesLoading(true);
      const response = await axios.get(CATEGORIES.ALL);
      if (response.data && response.data.success && response.data.data && Array.isArray(response.data.data)) {
        const mappedCategories = response.data.data.map(cat => ({
          ...cat,
          name: cat.title || cat.name,
          title: cat.title
        }));
        setCategories([{ _id: 'all', name: 'All Categories', title: 'All Categories' }, ...mappedCategories]);
      } else {
        setCategories(dummyCategories);
      }
    } catch (error) {
      console.error('Error fetching categories, using dummy data:', error);
      setCategories(dummyCategories);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // Initial categories fetch
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const fetchGoldPrices = async () => {
    const dummyPrices = {
      "22K": 6850,
      "18K": 5640,
      "24K": 7450
    };

    try {
      const response = await axios.get(OTHER.GOLD_PRICE);
      if (Array.isArray(response.data) && response.data.length > 0) {
        const prices = {};
        response.data.forEach((item) => {
          prices[item.Carat] = item.TodayPricePerGram;
        });
        setCaratPrices(prices);
        setWsStatus("Connected");
        return prices;
      }
      setCaratPrices(dummyPrices);
      setWsStatus("Offline Mode");
      return dummyPrices;
    } catch (err) {
      console.error("Failed to fetch gold prices fallback to dummy:", err);
      setCaratPrices(dummyPrices);
      setWsStatus("Offline Mode");
      return dummyPrices;
    }
  };

  // Apply filters when filter selections change
  useEffect(() => {
    if (products.length === 0) return;

    let filtered = [...products];

    // Apply category filter
    if (selectedCategory !== "all") {
      console.log('🔍 FILTERING DEBUG:');
      console.log('Selected category:', selectedCategory);
      console.log('Total products before filter:', products.length);
      
      filtered = filtered.filter((product) => {
        // Create a flexible matching function that handles different category formats
        const normalize = (str) => str ? str.toString().toLowerCase().trim() : '';
        
        // Try exact match first
        let matches = product.category === selectedCategory;
        
        // If no exact match, try normalized comparison
        if (!matches) {
          const normalizedProductCategory = normalize(product.category);
          const normalizedSelectedCategory = normalize(selectedCategory);
          matches = normalizedProductCategory === normalizedSelectedCategory;
        }
        
        // If still no match, try to handle common variations (singular vs plural)
        if (!matches) {
          const normalizedProductCategory = normalize(product.category);
          const normalizedSelectedCategory = normalize(selectedCategory);
          
          // Handle common singular/plural variations
          const variations = [
            normalizedSelectedCategory,
            normalizedSelectedCategory + 's', // Add 's'
            normalizedSelectedCategory.slice(0, -1), // Remove 's'
          ];
          
          matches = variations.includes(normalizedProductCategory);
        }
        
        return matches;
      });
      
      console.log('Products after filtering:', filtered.length);
      console.log('Filtered products:', filtered.map(p => ({ name: p.name, category: p.category })));
    }

    // Apply carat filter
    if (selectedCarat !== "all") {
      filtered = filtered.filter((product) => {
        const productCarat = (product.carat || "").toUpperCase();
        return productCarat === selectedCarat;
      });
    }

    setFilteredProducts(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [selectedCategory, selectedCarat, products]);

  // Setup polling as a fallback for WebSocket
  const setupPolling = useCallback(() => {
    console.log("Setting up polling for gold price updates");
    setUsePolling(true);
    setWsStatus("Using polling (30s interval)");

    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Fetch immediately and then set up interval
    fetchGoldPrices();

    pollingIntervalRef.current = setInterval(async () => {
      console.log("Polling for gold price update");
      await fetchGoldPrices();
    }, 30000); // Poll every 30 seconds

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Set up WebSocket with fallback to polling
  const setupWebSocket = useCallback(() => {
    // Temporarily disable WebSocket to prevent connection errors
    console.log("WebSocket disabled, using polling only");
    return setupPolling();
  }, [setupPolling]);

  // Initial setup on component mount
  useEffect(() => {
    console.log("Component mounted, initializing...");

    const initializeData = async () => {
      // Fetch data in parallel
      await Promise.all([
        fetchGoldPrices(),
        fetchProducts(),
        fetchCategories(),
      ]);

      // Calculate prices if all data is loaded
      if (Object.keys(caratPrices).length > 0 && products.length > 0) {
        calculateProductPrices(products, caratPrices);
      }
    };

    initializeData();
    const cleanup = setupWebSocket();

    return () => {
      console.log("Component unmounting, cleaning up...");
      if (typeof cleanup === "function") {
        cleanup();
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [setupWebSocket]);

  // Recalculate prices when gold prices or products change
  useEffect(() => {
    if (Object.keys(caratPrices).length > 0 && products.length > 0) {
      calculateProductPrices(products, caratPrices);
    }
  }, [caratPrices, products]);

  // Manual refresh button handler
  const handleManualRefresh = async () => {
    await fetchGoldPrices();
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedCategory("all");
    setSelectedCarat("all");
    updateURL("all", "all");
  };

  // Get current products for pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Next page
  const nextPage = () => {
    if (currentPage < Math.ceil(filteredProducts.length / productsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Previous page
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Get list of carats from API data
  const caratList = Object.keys(caratPrices).sort((a, b) => {
    // Sort by carat value (extract number from string like "24K")
    const caratA = parseInt(a.replace(/\D/g, ""));
    const caratB = parseInt(b.replace(/\D/g, ""));
    return caratB - caratA; // Sort highest to lowest
  });

  // Get unique carats from products
  const uniqueCarats = [
    ...new Set(products.map((product) => (product.carat || "").toUpperCase())),
  ].filter(Boolean);

  const addToWishlist = async (productId) => {
    try {
      const userId = "67f80c0de5b37dc266e25746";
      const response = await axios.post(
        getApiUrl("/wishlist/wishlist"),
        { productId, userId },
        {
          headers: {
            "Content-Type": "application/json",
            // Add authorization header if needed
            // 'Authorization': `Bearer ${token}`
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error adding to wishlist:", error);
      throw error;
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      // Assuming you have a DELETE endpoint
      const response = await axios.delete(
        getApiUrl(`/wishlist/wishlist/${productId}`), // TODO: Update to use WISHLIST.USER
        {
          headers: {
            // Add authorization header if needed
            // 'Authorization': `Bearer ${token}`
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error removing from wishlist:", error);
      throw error;
    }
  };

  const fetchUserWishlist = async () => {
    try {
      const response = await axios.get(
        getApiUrl("/wishlist/wishlist"),
        {
          headers: {
            // Add authorization header if needed
            // 'Authorization': `Bearer ${token}`
          },
        }
      );

      if (response.data && response.data.items) {
        const wishlistProductIds = response.data.items.map(
          (item) => item.productId
        );
        setFavorites(new Set(wishlistProductIds));
      }
    } catch (error) {
      console.error("Error fetching wishlist:", error);
    }
  };

  return (
    <div className="bg-[#fdfaf5] min-h-screen font-sans border-t border-[#D4AF37]/20 relative z-0">
      {/* Gold Price Banner */}
      {Object.keys(caratPrices).length > 0 && (
        <div className="w-full bg-[#cca34a] text-white py-2.5 px-4 shadow-sm flex items-center justify-center font-medium text-xs md:text-sm tracking-wide">
          <span className="mr-6 font-bold hidden md:inline tracking-wider">Today's Rates:</span>
          <div className="flex gap-4 md:gap-8 items-center justify-center flex-wrap">
            {caratList.slice(0, 3).map((carat) => (
              <span key={carat} className="flex items-center gap-1.5 opacity-95">
                 <span className="font-semibold">{carat}</span>
                 <span className="font-light tracking-wide">₹{parseFloat(caratPrices[carat]).toLocaleString()}/g</span>
              </span>
            ))}
          </div>
          <button onClick={handleManualRefresh} className="ml-6 hover:text-[#f3e5cd] transition-colors" title="Refresh Prices">
             <svg className="w-3 h-3 md:w-3.5 md:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          </button>
        </div>
      )}

      {/* Main Content Container */}
      <div className="max-w-[1300px] mx-auto px-4 md:px-8 lg:px-12 py-10 w-full relative z-10 bg-white shadow-xl mt-[-5px] md:mt-0 min-h-screen">
        
        {/* Breadcrumb / Back Navigation */}
        <div className="flex items-center text-gray-500 mb-8 cursor-pointer hover:text-black transition-colors w-fit" onClick={() => navigate(-1)}>
          <ChevronLeft className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
          <span className="text-sm font-light tracking-wide">Back to Collections</span>
        </div>

        {/* Page Header */}
        <div className="mb-10">
           <h1 className="text-3xl md:text-[2.6rem] font-serif text-[#C19A5B] mb-2.5 tracking-wide leading-tight mt-1">
             {selectedCategory === 'all' ? 'All Collections' : categories.find(c => c.name === selectedCategory || c.title === selectedCategory)?.name || selectedCategory}
           </h1>
           <p className="text-gray-500 font-light text-sm tracking-wide">{filteredProducts.length} pieces available</p>
        </div>

        {/* Filters Row */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-10 gap-6">
          <div className="flex flex-wrap gap-3.5">
             <button className="px-5 py-2.5 rounded-full border border-gray-200 bg-gray-50 text-gray-600 text-xs tracking-wider transition-colors hover:border-[#C19A5B] font-medium">Under ₹50k</button>
             <button className="px-5 py-2.5 rounded-full border border-gray-200 bg-gray-50 text-gray-600 text-xs tracking-wider transition-colors hover:border-[#C19A5B] font-medium">Ready to Ship</button>
             <button className="px-5 py-2.5 rounded-full border border-gray-200 bg-gray-50 text-gray-600 text-xs tracking-wider transition-colors hover:border-[#C19A5B] font-medium">Best Sellers</button>
             <button className="px-5 py-2.5 rounded-full border border-gray-200 bg-gray-50 text-gray-600 text-xs tracking-wider transition-colors hover:border-[#C19A5B] font-medium">New Arrivals</button>
          </div>

          <div className="flex items-center justify-between lg:justify-end gap-6 w-full lg:w-auto border-t lg:border-t-0 pt-5 lg:pt-0 border-gray-100">
             <button className="flex items-center gap-2 px-5 py-2.5 border border-[#C19A5B] text-[#C19A5B] bg-white text-sm font-medium hover:bg-[#fbf9f4] transition-colors focus:outline-none">
               <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} />
               Refine Filters
             </button>
             <span className="text-gray-500 text-xs md:text-sm font-medium tracking-wide">Showing {currentProducts.length} products</span>
          </div>
        </div>

        {/* Loading and Error States */}
        {isLoading && (
           <div className="flex justify-center items-center py-20">
             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C19A5B]"></div>
           </div>
        )}
        {error && <p className="text-red-500 text-center py-10 tracking-wide font-light">{error}</p>}
        {!isLoading && filteredProducts.length === 0 && (
          <p className="text-center py-16 text-gray-400 font-serif text-lg">No precious pieces found matching your criteria. Please adjust your filters.</p>
        )}

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
           {currentProducts.map((product) => (
             <div key={product._id} className="group bg-white flex flex-col mb-2">
               
               {/* Image Container */}
               <div 
                 className="relative w-full aspect-[4/5] bg-[#f9f9f9] overflow-hidden cursor-pointer"
                 onClick={() => navigate(`/product/${product._id}`)}
               >
                 <img
                    src={product.images?.[0] || product.coverImage || "https://images.unsplash.com/photo-1599643478524-fb66f70d00f8?auto=format&fit=crop&q=80&w=600"}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out"
                 />
                 
                 {/* Subtle dark overlay on hover */}
                 <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-500"></div>
                 
                 {/* Wishlist Heart */}
                 <button
                    onClick={(e) => toggleFavorite(product._id, e)}
                    className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm hover:scale-110 transition-transform duration-200"
                    title={favorites.has(product._id) ? "Remove from wishlist" : "Add to wishlist"}
                 >
                    <Heart size={16} strokeWidth={2} className={favorites.has(product._id) ? "fill-[#cca34a] text-[#cca34a]" : "text-gray-300 hover:text-gray-400"} />
                 </button>
                 
                 {/* Badges */}
                 {product.isNew && (
                    <div className="absolute top-4 left-4 bg-[#C19A5B] text-white text-[10px] uppercase font-bold tracking-widest px-2.5 py-1">
                      New
                    </div>
                 )}
               </div>

               {/* Product Details Area */}
               <div className="w-full border border-t-0 border-[#eae0d5]/60 p-5 md:p-6 flex flex-col gap-2 relative">
                 
                 {/* Top section: Title and subtitle */}
                 <div className="mb-2">
                   <h2 className="text-[#C19A5B] font-medium text-[15px] cursor-pointer hover:text-[#a8864d] transition-colors truncate tracking-wide" onClick={() => navigate(`/product/${product._id}`)}>
                      {product.name}
                   </h2>
                   <p className="text-gray-400 text-[11px] uppercase tracking-widest mt-1">
                      {product.category || 'Jewellery'}
                   </p>
                 </div>
                 
                 {/* Price Section */}
                 <div className="mb-4">
                    {(calculatedPrices[product._id] || product.priceDetails) ? (
                      <span className="text-[#C19A5B] font-serif tracking-wide text-[1.35rem]">
                        ₹{parseFloat(calculatedPrices[product._id]?.total || product.priceDetails?.totalPrice || 0).toLocaleString()}
                      </span>
                    ) : (
                      product.price ? (
                         <span className="text-[#C19A5B] font-serif tracking-wide text-[1.35rem]">
                           ₹{product.price.toLocaleString()}
                         </span>
                      ) : (
                         <span className="text-gray-400 text-sm font-serif italic">Price upon request</span>
                      )
                    )}
                 </div>

                 {/* Weight Details Dropdown Toggle */}
                 <div className="border-t border-[#f2ece4] pt-4 mb-4">
                    <button className="flex justify-between items-center w-full text-xs text-gray-500 font-medium hover:text-black tracking-wide transition-colors">
                      Weight Details
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                 </div>

                 {/* CTA Button */}
                 <button 
                   onClick={() => navigate(`/product/${product._id}`)}
                   className="w-full bg-[#cca34a] hover:bg-[#b88c3a] text-white font-medium py-[11px] text-[11px] uppercase tracking-[0.15em] transition-colors shadow-sm"
                 >
                   View Details
                 </button>
               </div>
               
             </div>
           ))}
        </div>

        {/* Pagination Block */}
        {filteredProducts.length > 0 && Math.ceil(filteredProducts.length / productsPerPage) > 1 && (
          <div className="flex justify-center mt-20 pb-8border-t border-gray-100 pt-10">
            <div className="flex items-center gap-1.5">
              <button onClick={prevPage} disabled={currentPage === 1} className="px-3 py-1.5 text-xs tracking-wider uppercase text-gray-400 hover:text-[#C19A5B] disabled:opacity-50 transition-colors">Prev</button>
              
              <div className="flex gap-1 px-2">
                {Array.from({ length: Math.ceil(filteredProducts.length / productsPerPage) }).map((_, idx) => (
                  <button 
                    key={idx} 
                    onClick={() => paginate(idx + 1)} 
                    className={`w-8 h-8 flex items-center justify-center text-sm transition-colors ${currentPage === idx + 1 ? 'bg-[#C19A5B] text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>

              <button onClick={nextPage} disabled={currentPage === Math.ceil(filteredProducts.length / productsPerPage)} className="px-3 py-1.5 text-xs tracking-wider uppercase text-gray-400 hover:text-[#C19A5B] disabled:opacity-50 transition-colors">Next</button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ViewProductsPage;
